// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import ReportPageScaffold from '../components/ReportPageScaffold';
import DateRangeFilter from '../components/DateRangeFilter';
import { ReportLoadingState } from '../components/ReportLoadingState';
import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Download, Info, RefreshCw, SlidersHorizontal } from 'lucide-react';
import DashboardFilterModal from '../components/dashboard/DashboardFilterModal';
import { useStoreRegions } from '../hooks/useStoreRegions';
import { groomingReportAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import ImageLightbox from '../components/ImageLightbox';
import CustomReportPanel from '../components/CustomReportPanel';
import { getLocalYesterdayDateString } from '../utils/dateHelpers';
import { isAdminForCurrentProject } from '../utils/isAdminForCurrentProject';
import { useReportFilters } from '../hooks/useReportFilters';
import { PAGE_SIZE_OPTIONS as BASE_PAGE_SIZE_OPTIONS } from '../constants/reportPageSize';
import { buildCsv, downloadCsv } from '../utils/csv';
import { getImageViewUrl, getFullImageViewUrl } from '../utils/imageUrl';
import { useSortState, SortTh } from '../components/dashboard/useTableSort';


type ComplianceStatus =
  | 'fully_compliant'
  | 'mostly_compliant'
  | 'partially_compliant'
  | 'minimally_compliant'
  | 'non_compliant';

interface GroomingRecord {
  id: number;
  empCode: string;
  employeeName: string;
  storeName: string;
  storeCode: string;
  city: string;
  state: string;
  attendanceDate: string;
  imageUrl: string;
  processedAt: string;
  createdAt: string;
  faceDetected: boolean;
  groomingStatus: 'exceptionally_groomed' | 'well_groomed' | 'average' | 'below_average' | 'not_groomed' | 'unclear';
  groomingScore: number;
  // Denominator for the score. Newer records (post-deploy) carry an explicit
  // max set by the parser. Null on legacy/v1 records — denominator helper
  // falls back to schema-shape inference for those.
  groomingMaxScore: number | null;
  groomingNotes: string;
  hairGroomed: boolean;
  beardGroomed: boolean | null;
  professionalAppearance: boolean | null;
  skinFreshness: boolean | null;
  attireNeatness: boolean | null;
  collarAndButtons: boolean | null;
  uniformCompliance: boolean | null;
  idBadgeVisible: boolean | null;
  smileAndConfidence: boolean | null;
  faceMatch: boolean | null;
  matchConfidence: string | null;
  matchMessage: string | null;
  faceMatchScore: number | null;
  awsFaceMatch: boolean | null;
  awsFaceMatchScore: number | null;
  awsMatchConfidence: string | null;
  awsMatchMessage: string | null;
  awsComparedAt: string | null;
  // Faces AWS detected in the selfie. NULL = unknown (row scored before the
  // column existed, or AWS never ran) — must NOT be rendered as 0, which is a
  // real value meaning "no face found".
  awsFaceCount: number | null;
  uniformDetected: boolean;
  uniformCondition: string;
  uniformNotes: string;
  backgroundType: string;
  backgroundCondition: string;
  backgroundNotes: string;
  appleLogoDetected: boolean;
  // Strict-prompt fields. `null` for legacy records; presence of `uniformSubFactors`
  // is the canonical signal that this row was scored under the strict 7-factor schema.
  uniformStatus: ComplianceStatus | null;
  uniformScore: number | null;
  // Key-agnostic: the sub-factor set is project-dependent. The default prompt
  // emits isWorkwear/logoPresent/logoClearlyVisible/garmentCondition/wornCorrectly,
  // but a project's custom prompt may emit a different set (e.g. Apple/170 emits
  // isWorkwear/wornCorrectly/footwearProfessional/garmentCondition and no logo
  // keys). Render whatever keys the record carries — never hard-code a fixed set.
  uniformSubFactors: Record<string, number> | null;
  garmentType: string | null;
  garmentColor: string | null;
  logoVisible: boolean | null;
  logoLocation: string | null;
  logoClarity: string | null;
  backgroundStatus: ComplianceStatus | null;
  backgroundScore: number | null;
  // Key-agnostic like uniformSubFactors — render whatever keys the record carries.
  backgroundSubFactors: Record<string, number> | null;
  isWorkplace: boolean | null;
  productDisplayDetected: boolean | null;
  productDisplayType: string | null;
  productDisplayBrandRelevant: boolean | null;
}


// Per-record overall compliance score (0–10). Mirrors the per-record step of
// the mobile grooming-batch composite (HRScreen.tsx:572-594) — average of the
// pillars actually scored on this record, with missing pillars dropped rather
// than scored 0 so legacy records without uniform/background don't drag down.
// Returns null when no pillar can be scored (UI renders an em-dash).
const computeRecordScore = (r: GroomingRecord): number | null => {
  // Composite = mean of ONLY Uniform Compliance + Background (both 0–10).
  // Grooming-factor and Face Match pillars are intentionally excluded.
  // Keep this in parity with the mobile HRScreen (buildRecordScore) and
  // apps/api apple-scorecard.service.ts (loadGroomingAvg).
  const uniformNorm = typeof r.uniformScore === 'number' ? r.uniformScore : null;
  const backgroundNorm = typeof r.backgroundScore === 'number' ? r.backgroundScore : null;
  const pillars = [uniformNorm, backgroundNorm].filter(
    (v): v is number => typeof v === 'number' && !isNaN(v),
  );
  if (pillars.length === 0) return null;
  return pillars.reduce((s, v) => s + v, 0) / pillars.length;
};

type GroomingTierLabel = 'Gold' | 'Silver' | 'Bronze' | 'No Tier';

const tierForScore = (score: number): { label: GroomingTierLabel; className: string } => {
  if (score >= 9) return { label: 'Gold', className: 'bg-amber-100 text-amber-800' };
  if (score >= 8) return { label: 'Silver', className: 'bg-gray-200 text-gray-700' };
  if (score >= 7) return { label: 'Bronze', className: 'bg-orange-100 text-orange-800' };
  return { label: 'No Tier', className: 'bg-gray-100 text-gray-500' };
};

const COMPLIANCE_LABELS: Record<ComplianceStatus, string> = {
  fully_compliant: 'Fully Compliant',
  mostly_compliant: 'Mostly Compliant',
  partially_compliant: 'Partially Compliant',
  minimally_compliant: 'Minimally Compliant',
  non_compliant: 'Non-Compliant',
};

const COMPLIANCE_CLASSES: Record<ComplianceStatus, string> = {
  fully_compliant: 'bg-green-100 text-green-800',
  mostly_compliant: 'bg-emerald-100 text-emerald-800',
  partially_compliant: 'bg-yellow-100 text-yellow-800',
  minimally_compliant: 'bg-orange-100 text-orange-800',
  non_compliant: 'bg-red-100 text-red-800',
};

const getComplianceBadge = (
  status: ComplianceStatus | null,
  score: number | null,
  legacyFallback: { label: string; className: string },
) => {
  if (!status) return legacyFallback;
  const scoreLabel = score != null ? ` (${score}/10)` : '';
  return {
    label: `${COMPLIANCE_LABELS[status]}${scoreLabel}`,
    className: COMPLIANCE_CLASSES[status],
  };
};

const getFaceMatchBadge = (faceMatch: boolean | null) => {
  return faceMatch === true
    ? { label: 'Match', className: 'bg-green-100 text-green-800' }
    : { label: 'No Match', className: 'bg-red-100 text-red-800' };
};

const getFaceMatchReason = (record: GroomingRecord): string => {
  if (record.matchMessage) return record.matchMessage;
  return record.faceMatch ? 'Face matched with profile photo.' : 'Face did not match profile photo.';
};

const formatSubFactor = (label: string, value: number): string => {
  const verdict = value === 2 ? 'Pass' : value === 1 ? 'Partial' : 'Fail';
  return `${label}: ${verdict}`;
};

// Friendly labels for the 0/1/2 sub-factor keys. The set of keys is
// project-dependent (a project's custom grooming prompt may add/drop keys), so
// rendering iterates over whatever the record carries and falls back to a
// Title-cased auto-label for any key not listed here. Mirrors the mobile
// SubFactorRow FACTOR_LABELS + friendlyLabel approach so both surfaces stay in
// parity as new keys (e.g. footwearProfessional) appear.
const SUBFACTOR_LABELS: Record<string, string> = {
  // Uniform
  isWorkwear: 'Workwear',
  logoPresent: 'Logo present',
  logoClearlyVisible: 'Logo clear',
  footwearProfessional: 'Footwear professional',
  garmentCondition: 'Condition',
  wornCorrectly: 'Worn correctly',
  // Background
  isRetailStore: 'Retail store',
  productDisplaysVisible: 'Product displays',
  brandSignageVisible: 'Brand signage',
  cleanAndOrganized: 'Clean & organized',
  retailLighting: 'Retail lighting',
};

const friendlySubFactorLabel = (key: string): string => {
  if (SUBFACTOR_LABELS[key]) return SUBFACTOR_LABELS[key];
  // Fallback: insert spaces before capitals, capitalise first letter.
  const spaced = key.replace(/([A-Z])/g, ' $1').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// Render every numeric sub-factor the record actually carries, in payload order.
// Non-numeric values are skipped so a malformed key can't render as a false "Fail".
const formatSubFactors = (sf: Record<string, number>): string =>
  Object.entries(sf)
    .filter(([, v]) => typeof v === 'number')
    .map(([key, v]) => formatSubFactor(friendlySubFactorLabel(key), v))
    .join(' • ');

const getUniformReason = (record: GroomingRecord): string => {
  // Strict records: show notes + garment detail + sub-factor breakdown.
  if (record.uniformSubFactors) {
    const lines: string[] = [];
    // AI "Notes" prose intentionally omitted from the report — keep the structured
    // garment/logo detail + sub-factor breakdown only.
    if (record.garmentType || record.garmentColor) {
      lines.push(`Garment: ${[record.garmentColor, record.garmentType].filter(Boolean).join(' ')}`);
    }
    if (record.logoVisible !== null) {
      lines.push(`Logo: ${record.logoVisible ? `visible (${record.logoLocation || 'unknown location'}, ${record.logoClarity || 'unknown clarity'})` : 'not visible'}`);
    }
    lines.push(formatSubFactors(record.uniformSubFactors));
    // Join with newlines so bulleted uniformNotes + the Garment/Logo lines + the
    // sub-factor summary each sit on their own line (popover uses whitespace-pre-line).
    return lines.join('\n');
  }
  // Legacy records: keep existing behaviour.
  if (record.uniformNotes) return record.uniformNotes;
  if (!record.uniformDetected) return 'No uniform or professional attire detected in the image.';
  return record.uniformCondition === 'proper'
    ? 'Uniform detected and in proper condition.'
    : record.uniformCondition === 'improper'
    ? 'Uniform detected but condition is not proper.'
    : 'Uniform detection was unclear.';
};

const getBackgroundReason = (record: GroomingRecord): string => {
  // Strict records: show notes + workplace verdict + sub-factor breakdown.
  if (record.backgroundSubFactors) {
    const lines: string[] = [];
    // AI "Notes" prose intentionally omitted from the report — keep the structured
    // workplace/type detail + sub-factor breakdown only.
    if (record.backgroundType && record.backgroundType !== 'unknown') {
      lines.push(`Type: ${record.backgroundType}${record.isWorkplace === false ? ' (not workplace)' : ''}`);
    }
    lines.push(formatSubFactors(record.backgroundSubFactors));
    // Join with newlines so bulleted backgroundNotes + Type line + sub-factor
    // summary each sit on their own line (popover uses whitespace-pre-line).
    return lines.join('\n');
  }
  // Legacy records: keep existing behaviour.
  if (record.backgroundNotes) return record.backgroundNotes;
  const typeLabel = record.backgroundType !== 'unknown' ? `Background type: ${record.backgroundType}. ` : '';
  const conditionLabel = record.backgroundCondition === 'clean'
    ? 'Background is clean and organized.'
    : record.backgroundCondition === 'cluttered'
    ? 'Background appears cluttered.'
    : 'Background condition unclear.';
  return typeLabel + conditionLabel;
};

// CSV export column order. Shared by the download so the header stays in lockstep
// with groomingRecordToCsvRow below.
const GROOMING_CSV_HEADERS = [
  'Employee Code',
  'Employee Name',
  'Store Name',
  'Store Code',
  'City',
  'State',
  'Date',
  'Face Match Verification',
  'Uniform Compliance',
  'Background Compliance',
  'Overall Compliance',
  'Image Link',
];

// Map one grooming record to its CSV row. Extracted to module scope so the
// full-range export (which walks every server page) produces byte-identical rows
// to what the on-screen table would render.
const groomingRecordToCsvRow = (record: GroomingRecord): string[] => {
  const uniformLabel = record.uniformStatus
    ? `${COMPLIANCE_LABELS[record.uniformStatus]}${record.uniformScore != null ? ` (${record.uniformScore}/10)` : ''}`
    : record.uniformDetected ? (record.uniformCondition === 'proper' ? 'Compliant' : 'Non-Compliant') : 'Not Detected';
  const backgroundLabel = record.backgroundStatus
    ? `${COMPLIANCE_LABELS[record.backgroundStatus]}${record.backgroundScore != null ? ` (${record.backgroundScore}/10)` : ''}`
    : record.backgroundCondition === 'clean' ? 'Compliant'
    : record.backgroundCondition === 'cluttered' ? 'Non-Compliant'
    : 'Unclear';
  // Face-match verdict prefers AWS when present (canonical post-switchover).
  // Falls back to Gemini boolean for legacy records.
  const faceMatchVerdict = record.awsFaceMatchScore != null
    ? (record.awsFaceMatch ? 'Match' : 'No Match')
    : record.faceMatch !== null
      ? (record.faceMatch === true ? 'Match' : 'No Match')
      : '-';
  const overallRaw = computeRecordScore(record);
  const overallLabel = overallRaw != null
    ? (() => {
        const rounded = Math.round(overallRaw * 10) / 10;
        return `${tierForScore(rounded).label} (${rounded.toFixed(1)}/10)`;
      })()
    : '-';
  return [
    record.empCode,
    record.employeeName,
    record.storeName || '-',
    record.storeCode || '-',
    record.city || '-',
    record.state || '-',
    record.attendanceDate,
    faceMatchVerdict,
    uniformLabel,
    backgroundLabel,
    overallLabel,
    getFullImageViewUrl(record.imageUrl),
  ];
};

// Client-side search predicate — matches the same superset of fields the report
// table filters on (server search covers emp code / store / name; this adds
// city, state, date and the computed compliance/face-match/tier labels). Shared
// by both the on-screen filter and the full-range export so the CSV reflects
// exactly what the user sees. Empty query matches everything.
const matchesGroomingSearch = (record: GroomingRecord, rawQuery: string): boolean => {
  const query = rawQuery.toLowerCase().trim();
  if (!query) return true;
  const faceMatchLabel = record.awsFaceMatchScore != null
    ? (record.awsFaceMatch ? 'match' : 'no match')
    : record.faceMatch !== null
      ? (record.faceMatch === true ? 'match' : 'no match')
      : '';
  const uniformLabel = record.uniformStatus
    ? COMPLIANCE_LABELS[record.uniformStatus].toLowerCase()
    : record.uniformDetected ? (record.uniformCondition === 'proper' ? 'compliant' : 'non-compliant') : 'not detected';
  const backgroundLabel = record.backgroundStatus
    ? COMPLIANCE_LABELS[record.backgroundStatus].toLowerCase()
    : record.backgroundCondition === 'clean' ? 'compliant'
    : record.backgroundCondition === 'cluttered' ? 'non-compliant'
    : 'unclear';
  const storeCodeLabel = (record.storeCode || '').toLowerCase();
  const overallRaw = computeRecordScore(record);
  const overallLabel = overallRaw != null
    ? tierForScore(Math.round(overallRaw * 10) / 10).label.toLowerCase()
    : '';
  return (
    record.empCode?.toLowerCase().includes(query) ||
    record.employeeName?.toLowerCase().includes(query) ||
    record.storeName?.toLowerCase().includes(query) ||
    record.storeCode?.toLowerCase().includes(query) ||
    record.city?.toLowerCase().includes(query) ||
    record.state?.toLowerCase().includes(query) ||
    record.attendanceDate?.toLowerCase().includes(query) ||
    faceMatchLabel.includes(query) ||
    uniformLabel.includes(query) ||
    backgroundLabel.includes(query) ||
    overallLabel.includes(query) ||
    storeCodeLabel.includes(query)
  );
};

function ReasonPopover({ reason, children }: { reason: React.ReactNode; children: React.ReactNode }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  const showTooltip = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 6,
      left: rect.left + rect.width / 2,
    });
  };

  const hideTooltip = () => setPos(null);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        className="cursor-pointer"
      >
        {children}
      </span>
      {pos && createPortal(
        <div
          className="fixed z-50 w-64 whitespace-pre-line px-3 py-2 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{ top: pos.top, left: pos.left, transform: 'translateX(-50%)' }}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
        >
          {reason}
        </div>,
        document.body
      )}
    </>
  );
}

export default function GroomingReportPage() {
  const { user, selectedProjectId } = useAuthStore();
  const [groomingData, setGroomingData] = useState<GroomingRecord[]>([]);
  // Exact count of ALL rows matching the current filters across the whole date
  // range — sent by the backend on page 1 and cached across page navigation.
  // Drives "Showing X–Y of N" + Prev/Next so the report can page through every
  // record instead of stopping at the first server page.
  const [total, setTotal] = useState<number | null>(null);
  // True while the Download button is walking every server page to assemble the
  // full-range CSV (the table only holds one page in memory at a time).
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [brokenImages, setBrokenImages] = useState<Set<number>>(new Set());
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  // "Regenerate scores" (bulk force re-analysis) state.
  const [regenerating, setRegenerating] = useState(false);
  const [regenMsg, setRegenMsg] = useState<string | null>(null);
  const [regenProgress, setRegenProgress] = useState<{ processed: number; total: number } | null>(null);
  const regenPollRef = useRef<number | null>(null);
  // Role filter — server-side (applies across all pages). Options come from the
  // report response (distinct role categories assigned in the project).
  const [roleFilter, setRoleFilter] = useState('');
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  // Store-attribute filters. This report is SERVER-paged, so unlike the other
  // reports these are sent as query params and the options come from the API
  // (the visible page is not the whole result set).
  const [storeAttrOpts, setStoreAttrOpts] = useState<{ trades: string[]; categories: string[]; cities: string[]; states: string[] }>(
    { trades: [], categories: [], cities: [], states: [] },
  );
  const [tradeFilter, setTradeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [storeFiltersOpen, setStoreFiltersOpen] = useState(false);
  const { regionOf, regionsFrom } = useStoreRegions();
  const regionOptions = useMemo(() => regionsFrom(storeAttrOpts.states), [storeAttrOpts.states, regionsFrom]);
  // A Region pick travels as its member states — the server has no state→region map.
  const regionStatesParam = useMemo(
    () => (regionFilter ? storeAttrOpts.states.filter((st) => regionOf(st) === regionFilter).join(',') || '__no_region_match__' : undefined),
    [regionFilter, storeAttrOpts.states, regionOf],
  );
  const storeFilterCount = [tradeFilter, categoryFilter, regionFilter, cityFilter].filter(Boolean).length;

  // Filters - Default to yesterday.
  // Date inputs work in a "pending → applied" model: changing the date input
  // only updates `startDate`/`endDate` (pending), which does NOT trigger a
  // fetch. The user clicks Apply to commit the pending values into
  // `appliedStartDate`/`appliedEndDate`, and only that copy triggers the
  // fetch. This eliminates the race where picking a 2-input range fired two
  // overlapping requests, the slow one finished last, and a stale empty
  // response overwrote the correct one. It also gives the user a single,
  // intentional moment to fire the backend's heavy work.
  //
  // Yesterday is the right landing default — most users review the previous
  // day's grooming submissions the morning after. Matches Daily Attendance /
  // Random Attendance / Task Response.
  //
  // The pending → applied date model + searchQuery/pageSize/currentPage are the
  // same state block the other report pages share verbatim — see
  // hooks/useReportFilters. pageSize and currentPage live separately from the
  // date-range model: flipping pages or changing rows per page is cheap and
  // fires immediately, while picking a new date range is heavy and requires
  // explicit Apply.
  const defaultDate = getLocalYesterdayDateString();
  const {
    startDate, setStartDate,
    endDate, setEndDate,
    appliedStartDate, appliedEndDate,
    searchQuery, setSearchQuery,
    pageSize, setPageSize,
    currentPage, setCurrentPage,
    hasPendingDateChanges,
    applyDateRange,
  } = useReportFilters({
    initialStartDate: defaultDate,
    initialEndDate: defaultDate,
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Column sort. The report is server-paginated, so the API sorts (sort_by /
  // sort_dir) — sorting only the page on screen would mislead.
  const columnSort = useSortState();

  // Grooming additionally offers an "All" cap (MAX_LIMIT) on top of the shared
  // base options. Spreading the shared base keeps the first six entries in
  // lockstep with the other report pages. The "All" option sends a server-side
  // cap since true unbounded reads are unsafe for very large tables.
  const PAGE_SIZE_OPTIONS: Array<{ label: string; value: number }> = [
    ...BASE_PAGE_SIZE_OPTIONS,
    { label: 'All', value: 5000 },
  ];

  const handlePageSizeChange = (next: number) => {
    setPageSize(next);
    setCurrentPage(1); // changing page size resets to first page
  };

  // Debounce searchQuery → debouncedSearch so backend SELECT doesn't fire on every keystroke.
  // Backend filtering is required (not just frontend) because the SQL has LIMIT 500 and
  // would otherwise truncate matching rows when other employees have newer rows in range.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Cancel-and-replace pattern: every dep change (date, project, search) aborts
  // the previous in-flight request at the network layer AND guards state writes
  // by token. We need both because:
  //  - AbortController stops the HTTP request immediately (prevents the slow
  //    backend from holding 5 minute connections that all resolve at once).
  //  - The token guard catches the edge where the abort lands after the
  //    response has already been parsed by axios but before our setState runs.
  // The backend is unaffected — the async grooming/face-match work it kicked
  // off continues even after the HTTP connection is severed.
  const fetchTokenRef = useRef(0);
  const fetchAbortRef = useRef<AbortController | null>(null);

  const fetchGroomingReport = async () => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    const myToken = ++fetchTokenRef.current;
    const debugTag = `[GroomingReport#${myToken}]`;
    const offset = (currentPage - 1) * pageSize;
    console.log(`${debugTag} fetch start`, {
      startDate: appliedStartDate,
      endDate: appliedEndDate,
      selectedProjectId,
      search: debouncedSearch,
      limit: pageSize,
      offset,
      currentPage,
    });
    setLoading(true);
    try {
      // Send employeeCode only for non-admin callers (see RandomAttendanceLogsPage for context).
      const isAdmin = isAdminForCurrentProject(user, selectedProjectId);

      const response = await groomingReportAPI.getReport(
        {
          start_date: appliedStartDate,
          end_date: appliedEndDate,
          employeeCode: isAdmin ? undefined : user?.userName,
          projectId: selectedProjectId ?? undefined,
          search: debouncedSearch.trim() || undefined,
          role_category: roleFilter || undefined,
          trades: tradeFilter || undefined,
          categories: categoryFilter || undefined,
          cities: cityFilter || undefined,
          states: regionStatesParam,
          limit: pageSize,
          offset,
          sort_by: columnSort.sortKey ?? undefined,
          sort_dir: columnSort.sortKey ? columnSort.sortDir : undefined,
        },
        { signal: controller.signal },
      );

      const records = response.data?.data || [];
      // Keep the role dropdown populated from the (filter-independent) list the
      // backend returns, so all options remain available after one is picked.
      if (Array.isArray(response.data?.availableRoleCategories)) {
        setAvailableRoles(response.data.availableRoleCategories);
      }
      // Filter-independent, so the dropdowns keep every option after one is picked.
      if (response.data?.availableStoreAttrs) {
        const a = response.data.availableStoreAttrs;
        setStoreAttrOpts({
          trades: Array.isArray(a.trades) ? a.trades : [],
          categories: Array.isArray(a.categories) ? a.categories : [],
          cities: Array.isArray(a.cities) ? a.cities : [],
          states: Array.isArray(a.states) ? a.states : [],
        });
      }
      console.log(`${debugTag} response arrived`, {
        latestToken: fetchTokenRef.current,
        myToken,
        willApply: fetchTokenRef.current === myToken && !controller.signal.aborted,
        recordCount: records.length,
        backendCount: response.data?.count,
        success: response.data?.success,
      });
      if (fetchTokenRef.current !== myToken || controller.signal.aborted) return;
      setGroomingData(records);
      // The backend returns an exact `total` only on page 1 (offset 0); it's
      // null on later pages, where we keep the cached total. Every filter change
      // resets to page 1, so the total refreshes exactly when the filter set
      // changes.
      if (typeof response.data?.total === 'number') setTotal(response.data.total);
    } catch (err: any) {
      // Aborts surface as canceled errors — they're expected, not a real failure.
      if (controller.signal.aborted || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        console.log(`${debugTag} aborted (newer fetch took over)`);
        return;
      }
      console.error(`${debugTag} fetch failed`, err);
      if (fetchTokenRef.current !== myToken) return;
      setGroomingData([]);
    } finally {
      if (fetchTokenRef.current === myToken && !controller.signal.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroomingReport();
  }, [appliedStartDate, appliedEndDate, pageSize, currentPage, selectedProjectId, debouncedSearch, roleFilter, tradeFilter, categoryFilter, cityFilter, regionStatesParam, columnSort.sortKey, columnSort.sortDir]);

  // Search / project / role / date-range changes implicitly reset to page 1 —
  // otherwise the user changes a filter and lands on page 5 of a different
  // result set. Resetting to page 1 also re-requests the exact range total
  // (backend computes it at offset 0 only), so "of N" stays correct.
  // A new column sort re-orders every page, so it starts again at page 1 too.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedProjectId, roleFilter, tradeFilter, categoryFilter, cityFilter, regionStatesParam, appliedStartDate, appliedEndDate, columnSort.sortKey, columnSort.sortDir]);

  // Download EVERY matching record for the selected date range — not just the
  // page currently on screen. Because the report is server-side paginated, the
  // table only holds one page in memory, so a naive export of the loaded rows
  // silently dropped everything past the current page. Here we walk the whole
  // range in server-capped pages (MAX_LIMIT = 5000), applying the exact same
  // server + client-side filters as the report, and build the CSV from the full
  // accumulated set. Only the light CSV rows are retained between pages, so peak
  // memory stays bounded even on a 5000-employee month.
  const EXPORT_PAGE_SIZE = 5000; // matches backend MAX_LIMIT
  // Synchronous re-entrancy guard. The `exporting` state alone can't block a
  // same-frame double-click — setExporting only schedules a re-render, so both
  // click handlers still see the stale closure value and both would start a
  // full-range walk. Mirrors the fetch path's ref-guard pattern.
  const exportingRef = useRef(false);
  const handleExportCSV = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setExporting(true);
    try {
      const isAdmin = isAdminForCurrentProject(user, selectedProjectId);
      // Capture the COMMITTED search once so the server query and the client
      // re-filter use the same term. Using the live searchQuery for the client
      // side while sending debouncedSearch to the server would, during the 350ms
      // debounce window, fetch with the old term but re-filter with the new one
      // — silently dropping/omitting rows from the CSV.
      const searchTerm = debouncedSearch.trim();
      const csvData: string[][] = [];
      let offset = 0;
      for (;;) {
        const resp = await groomingReportAPI.getReport({
          start_date: appliedStartDate,
          end_date: appliedEndDate,
          employeeCode: isAdmin ? undefined : user?.userName,
          projectId: selectedProjectId ?? undefined,
          search: searchTerm || undefined,
          role_category: roleFilter || undefined,
          trades: tradeFilter || undefined,
          categories: categoryFilter || undefined,
          cities: cityFilter || undefined,
          states: regionStatesParam,
          limit: EXPORT_PAGE_SIZE,
          offset,
          // Same order as the table on screen.
          sort_by: columnSort.sortKey ?? undefined,
          sort_dir: columnSort.sortKey ? columnSort.sortDir : undefined,
        });
        const page: GroomingRecord[] = resp.data?.data || [];
        for (const record of page) {
          if (matchesGroomingSearch(record, searchTerm)) csvData.push(groomingRecordToCsvRow(record));
        }
        if (page.length < EXPORT_PAGE_SIZE) break;
        offset += EXPORT_PAGE_SIZE;
      }
      // Name the file with the APPLIED range that the data was actually fetched
      // with — not the pending date inputs, which may hold un-applied edits.
      downloadCsv(`grooming-report-${appliedStartDate}-to-${appliedEndDate}.csv`, buildCsv(GROOMING_CSV_HEADERS, csvData));
    } catch (err) {
      console.error('[GroomingReport] full-range export failed', err);
    } finally {
      exportingRef.current = false;
      setExporting(false);
    }
  };

  // Force re-score EVERY grooming record in the current project + date range
  // under the project's current prompt (overwrites existing scores). Runs in the
  // background server-side; we refresh the table after a short delay.
  const handleRegenerate = async () => {
    if (!selectedProjectId) {
      setRegenMsg('Select a project first.');
      return;
    }
    const rangeLabel = appliedStartDate === appliedEndDate
      ? appliedStartDate
      : `${appliedStartDate} to ${appliedEndDate}`;
    // Regenerate honours the CURRENT filters (search by code/name/store + role),
    // so it re-scores only the records you've filtered to. No filters = all.
    const searchScope = debouncedSearch.trim();
    const filterDesc =
      (searchScope ? ` matching "${searchScope}"` : '') +
      (roleFilter ? `${searchScope ? ' and' : ''} in role "${roleFilter}"` : '');
    const scopeText = filterDesc ? `the grooming records${filterDesc}` : 'ALL grooming records';
    const ok = window.confirm(
      `Re-score ${scopeText} for ${rangeLabel} using the current grooming prompt?\n\n` +
      `This also re-runs AWS face verification for those records. It overwrites their ` +
      `existing scores and cannot be undone. It runs in the background (~1-2 minutes).`,
    );
    if (!ok) return;

    setRegenerating(true);
    setRegenMsg(null);
    setRegenProgress(null);
    if (regenPollRef.current) { window.clearInterval(regenPollRef.current); regenPollRef.current = null; }
    try {
      const resp = await groomingReportAPI.bulkReanalyze({
        projectId: selectedProjectId,
        startDate: appliedStartDate,
        endDate: appliedEndDate,
        search: debouncedSearch.trim() || undefined,
        roleCategory: roleFilter || undefined,
      });
      const total: number = resp.data?.queued ?? 0;
      const jobId: string | undefined = resp.data?.jobId;
      setRegenMsg(resp.data?.message || `Re-scoring started for ${total} record(s).`);

      if (total > 0 && jobId) {
        setRegenProgress({ processed: 0, total });
        // Poll the job until done, then refresh the table.
        regenPollRef.current = window.setInterval(async () => {
          try {
            const p = await groomingReportAPI.reanalyzeProgress(jobId);
            const processed: number = p.data?.processed ?? 0;
            const t: number = p.data?.total ?? total;
            setRegenProgress({ processed, total: t });
            if (p.data?.done) {
              if (regenPollRef.current) { window.clearInterval(regenPollRef.current); regenPollRef.current = null; }
              const errored: number = p.data?.errored ?? 0;
              setRegenProgress(null);
              // Face match is NOT guaranteed to re-run: AWS is skipped when
              // Rekognition is disabled server-side, the row has no reference
              // photo, or the selfie/profile download fails. This banner used to
              // claim "including face match" unconditionally, which made a
              // skipped run look successful while the face verdict stayed stale.
              // Report what the server actually did.
              const ran: number = p.data?.faceMatchRan ?? 0;
              const skipReasons: Record<string, number> = p.data?.faceMatchSkipReasons ?? {};
              const reasonText = Object.entries(skipReasons)
                .sort((a, b) => b[1] - a[1])
                .map(([reason, n]) => `${n} × ${reason}`)
                .join('; ');
              const faceMatchText =
                ran === t && t > 0
                  ? ', including face match'
                  : ran > 0
                    ? `. Face match re-run on ${ran} of ${t}${reasonText ? ` — remainder: ${reasonText}` : ''}`
                    : `. ⚠️ Face match was NOT re-run${reasonText ? ` — ${reasonText}` : ''}`;
              setRegenMsg(
                `Re-scored ${processed} of ${t} record(s)${errored ? ` (${errored} failed)` : ''} under the current prompt${faceMatchText}. Refreshed.`,
              );
              fetchGroomingReport();
            }
          } catch {
            // Job expired or hit a different instance — stop the bar; the
            // background work still completes server-side.
            if (regenPollRef.current) { window.clearInterval(regenPollRef.current); regenPollRef.current = null; }
            setRegenProgress(null);
            setRegenMsg('Re-scoring is running in the background. Refresh in a minute to see updated scores.');
          }
        }, 2500);
      }
    } catch (err: any) {
      setRegenMsg(err?.response?.data?.message || 'Failed to start re-scoring. Please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  // Stop polling if the user leaves the page mid-run.
  useEffect(() => {
    return () => {
      if (regenPollRef.current) { window.clearInterval(regenPollRef.current); regenPollRef.current = null; }
    };
  }, []);

  const canRegenerate = isAdminForCurrentProject(user, selectedProjectId);

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return groomingData;
    return groomingData.filter(record => matchesGroomingSearch(record, searchQuery));
  }, [groomingData, searchQuery]);

  return (
    <CustomReportPanel
      sourceKey="grooming"
      sourceLabel="Grooming"
      promptPlaceholder='e.g. "Average grooming score per store last week, as a bar chart"'
    >
    <ReportPageScaffold
      title="Grooming Report"
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      searchPlaceholder="Search by employee code, name, store, city, status..."
      searchInputPaddingRight="pr-4"
      filters={
        <>
          <button
            type="button"
            onClick={() => setStoreFiltersOpen(true)}
            className={`flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm font-medium shadow-sm transition ${
              storeFilterCount > 0
                ? 'border-primary-200 bg-primary-50 text-primary-700'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
            title="Filter by store trade, category, region or city"
          >
            <SlidersHorizontal className="w-4 h-4" /> Filters
            {storeFilterCount > 0 && (
              <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs font-bold text-white">{storeFilterCount}</span>
            )}
          </button>

          {availableRoles.length > 0 && (
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              title="Filter employees by role"
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <option value="">All roles</option>
              {availableRoles.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          )}

          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            hasPendingDateChanges={hasPendingDateChanges}
            onStartDateChange={(e) => setStartDate(e.target.value)}
            onEndDateChange={(e) => setEndDate(e.target.value)}
            onStartDateKeyDown={(e) => { if (e.key === 'Enter' && hasPendingDateChanges) applyDateRange(); }}
            onEndDateKeyDown={(e) => { if (e.key === 'Enter' && hasPendingDateChanges) applyDateRange(); }}
            onApply={applyDateRange}
          />

          <button
            onClick={handleExportCSV}
            disabled={filteredData.length === 0 || exporting}
            title="Download every record for the selected date range"
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Preparing…' : 'Download'}
          </button>

          {canRegenerate && (
            <button
              onClick={handleRegenerate}
              disabled={regenerating || regenProgress !== null}
              title="Re-score every grooming record in this date range using the current grooming prompt"
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${regenerating || regenProgress ? 'animate-spin' : ''}`} />
              {regenerating || regenProgress ? 'Regenerating…' : 'Regenerate scores'}
            </button>
          )}
        </>
      }
    >
        {/* Regenerate-scores status banner */}
        {regenMsg && !regenProgress && (
          <div className="mt-4 flex items-start justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            <span>{regenMsg}</span>
            <button
              onClick={() => setRegenMsg(null)}
              className="shrink-0 text-blue-500 hover:text-blue-700"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {/* Regenerate-scores progress bar */}
        {regenProgress && (
          <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            <div className="mb-2 flex items-center justify-between font-medium">
              <span>
                Regenerating scores… {regenProgress.processed} / {regenProgress.total} employees
              </span>
              <span>
                {regenProgress.total > 0
                  ? Math.round((regenProgress.processed / regenProgress.total) * 100)
                  : 0}
                %
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-blue-100">
              <div
                className="h-full rounded-full bg-blue-500 transition-all duration-300"
                style={{
                  width: `${
                    regenProgress.total > 0
                      ? Math.min(100, (regenProgress.processed / regenProgress.total) * 100)
                      : 0
                  }%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && <ReportLoadingState />}

        {/* No Records Found */}
        {!loading && filteredData.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-gray-500">No records found</p>
          </div>
        )}

        {/* Table */}
        {!loading && filteredData.length > 0 && (
        <div className="overflow-hidden mt-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/70">
                <tr>
                  <SortTh label="EMPLOYEE CODE" sortKey="emp_code" sort={columnSort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                  <SortTh label="EMPLOYEE NAME" sortKey="employee_name" sort={columnSort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                  <SortTh label="STORE NAME" sortKey="store_name" sort={columnSort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                  <SortTh label="STORE CODE" sortKey="store_code" sort={columnSort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                  <SortTh label="DATE" sortKey="date" sort={columnSort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                  <SortTh label="FACE MATCH VERIFICATION" sortKey="face_match" sort={columnSort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                  <SortTh label="UNIFORM COMPLIANCE" sortKey="uniform" sort={columnSort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                  <SortTh label="BACKGROUND COMPLIANCE" sortKey="background" sort={columnSort} align="center" className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap" />
                  <SortTh
                    label="OVERALL COMPLIANCE"
                    sortKey="overall"
                    sort={columnSort}
                    align="center"
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap"
                    suffix={
                      <ReasonPopover
                        reason={
                          <div className="space-y-2 text-left">
                            <div className="font-semibold text-gray-900">How this score is calculated</div>
                            <div>Average of the two pillars below, each on a 0–10 scale. A pillar without data for a row is excluded (not scored as 0).</div>
                            <ul className="list-disc pl-4 space-y-0.5">
                              <li><span className="font-medium">Uniform</span> — compliance score (0–10)</li>
                              <li><span className="font-medium">Background</span> — compliance score (0–10)</li>
                            </ul>
                            <div className="pt-1 border-t border-gray-100">
                              <div className="font-semibold text-gray-900 mb-1">Tiers</div>
                              <div>Gold ≥ 9.0 · Silver ≥ 8.0 · Bronze ≥ 7.0 · No Tier &lt; 7.0</div>
                            </div>
                          </div>
                        }
                      >
                        <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                      </ReasonPopover>
                    }
                  />
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap">
                    SELFIE
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((record) => {
                    const imageUrl = getImageViewUrl(record.imageUrl);
                    const faceMatchBadge = getFaceMatchBadge(record.faceMatch);
                    const uniformBadge = getComplianceBadge(
                      record.uniformStatus,
                      record.uniformScore,
                      // Legacy fallback for old records
                      {
                        label: record.uniformDetected
                          ? (record.uniformCondition === 'proper' ? 'Compliant' : 'Non-Compliant')
                          : 'Not Detected',
                        className: record.uniformDetected
                          ? (record.uniformCondition === 'proper' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800')
                          : 'bg-gray-100 text-gray-600',
                      },
                    );
                    const backgroundBadge = getComplianceBadge(
                      record.backgroundStatus,
                      record.backgroundScore,
                      // Legacy fallback for old records
                      {
                        label: record.backgroundCondition === 'clean'
                          ? 'Compliant'
                          : record.backgroundCondition === 'cluttered' ? 'Non-Compliant' : 'Unclear',
                        className: record.backgroundCondition === 'clean'
                          ? 'bg-green-100 text-green-800'
                          : record.backgroundCondition === 'cluttered'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-600',
                      },
                    );
                    
                    return (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          {record.empCode}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center">
                          {record.employeeName || 'N/A'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          {record.storeName || '—'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          {record.storeCode || '—'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          {record.attendanceDate}
                        </td>
                        <td className="px-4 py-4 text-center align-middle">
                          {record.awsFaceMatchScore != null ? (
                            // AWS is the canonical face-match source from the AWS-switchover
                            // deploy onwards. When present (either auto-run on new records
                            // or admin-triggered on legacy records), it supersedes the
                            // legacy Gemini badge.
                            <ReasonPopover reason={
                              (record.awsMatchMessage || (record.awsFaceMatch ? 'Face matched with profile photo.' : 'Face did not match profile photo.')) +
                              // Only mentioned when it changes how the score should be read:
                              // with >1 face the score is the BEST of several, so a match no
                              // longer implies the employee was the subject of the photo.
                              (record.awsFaceCount != null && record.awsFaceCount > 1
                                ? ` Selfie contained ${record.awsFaceCount} faces — this is the closest-matching one, so the employee may not be the subject of the photo.`
                                : '')
                            }>
                              <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${
                                record.awsFaceMatch ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {record.awsFaceMatch ? 'Match' : 'No Match'} ({record.awsFaceMatchScore}%)
                                {/* Exception marker only — a normal single-subject selfie
                                    shows nothing extra, so the column stays scannable. */}
                                {record.awsFaceCount != null && record.awsFaceCount > 1 && (
                                  <span
                                    className="font-normal text-amber-700"
                                    title={`${record.awsFaceCount} faces detected in this selfie`}
                                  >
                                    · {record.awsFaceCount} faces
                                  </span>
                                )}
                              </span>
                            </ReasonPopover>
                          ) : record.faceMatch !== null ? (
                            // Legacy records pre-AWS-switchover. Gate on faceMatch boolean
                            // because many older rows have the boolean verdict without a
                            // stored similarityScore. Show the score in parens only if it
                            // was actually persisted.
                            <ReasonPopover reason={getFaceMatchReason(record)}>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${faceMatchBadge.className}`}>
                                {faceMatchBadge.label}
                                {record.faceMatchScore != null ? ` (${record.faceMatchScore}%)` : ''}
                              </span>
                            </ReasonPopover>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <ReasonPopover reason={getUniformReason(record)}>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${uniformBadge.className}`}>
                              {uniformBadge.label}
                            </span>
                          </ReasonPopover>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <ReasonPopover reason={getBackgroundReason(record)}>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${backgroundBadge.className}`}>
                              {backgroundBadge.label}
                            </span>
                          </ReasonPopover>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          {(() => {
                            const overallRaw = computeRecordScore(record);
                            if (overallRaw == null) {
                              return <span className="text-gray-400">—</span>;
                            }
                            const rounded = Math.round(overallRaw * 10) / 10;
                            const tier = tierForScore(rounded);
                            return (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${tier.className}`}>
                                {tier.label} — {rounded.toFixed(1)} / 10
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-4 text-sm text-center">
                          {imageUrl && !brokenImages.has(record.id) ? (
                            <div className="flex justify-center">
                              <button
                                onClick={() => setLightboxUrl(imageUrl)}
                                className="block"
                              >
                                <img
                                  src={imageUrl}
                                  alt="Selfie"
                                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                                  style={{ imageOrientation: 'from-image' }}
                                  onError={() => {
                                    setBrokenImages(prev => new Set(prev).add(record.id));
                                  }}
                                />
                              </button>
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Pagination footer. Only shown when there's at least one record on
            screen. Page size dropdown lives here (standard table UX); changing
            it resets to page 1 and re-fetches. When the backend supplies an
            exact range total (`total`, computed on page 1) Prev/Next and the
            "of N" counters are driven by it so the report pages through every
            record; if the total is unavailable we fall back to the old
            full-page heuristic. */}
        {!loading && filteredData.length > 0 && (() => {
          const totalPages = total != null ? Math.max(1, Math.ceil(total / pageSize)) : null;
          const isLastPage = totalPages != null ? currentPage >= totalPages : filteredData.length < pageSize;
          return (
          /* 3-column footer: rows-per-page on the left, page nav in the
             true center (matching flex-1 spacers keep it centered even when
             the left block widens), and the right column intentionally
             empty so nothing falls under the fixed SSI / "New custom
             report" widget at bottom-right. */
          <div className="mt-4 flex flex-wrap items-center gap-3 px-1">
            <div className="flex items-center gap-3 flex-1 min-w-[200px]">
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white"
                title="Rows per page"
              >
                {PAGE_SIZE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <span className="text-sm text-gray-600">
                {(() => {
                  const startRow = (currentPage - 1) * pageSize + 1;
                  const endRow = startRow + filteredData.length - 1;
                  return `Showing ${startRow}–${endRow}${total != null ? ` of ${total}` : ''}`;
                })()}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700 px-2">
                Page {currentPage}{totalPages != null ? ` of ${totalPages}` : ''}
              </span>
              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={isLastPage}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title={isLastPage ? 'No more rows' : 'Next page'}
              >
                Next
              </button>
            </div>
            <div className="flex-1" aria-hidden="true" />
          </div>
          );
        })()}
      <DashboardFilterModal
        open={storeFiltersOpen}
        onClose={() => setStoreFiltersOpen(false)}
        title="Filters"
        sections={[
          { kind: 'select', key: 'trade', label: 'Store Trade', value: tradeFilter, onChange: setTradeFilter,
            options: [{ value: '', label: 'All trades' }, ...storeAttrOpts.trades.map((t) => ({ value: t, label: t }))] },
          { kind: 'select', key: 'storeCategory', label: 'Store Category', value: categoryFilter, onChange: setCategoryFilter,
            options: [{ value: '', label: 'All categories' }, ...storeAttrOpts.categories.map((c) => ({ value: c, label: c }))] },
          { kind: 'select', key: 'region', label: 'Region', value: regionFilter, onChange: setRegionFilter,
            options: [{ value: '', label: 'All regions' }, ...regionOptions.map((r) => ({ value: r, label: r }))] },
          { kind: 'select', key: 'storeCity', label: 'City', value: cityFilter, onChange: setCityFilter,
            options: [{ value: '', label: 'All cities' }, ...storeAttrOpts.cities.map((c) => ({ value: c, label: c }))] },
        ]}
        activeCount={storeFilterCount}
        onClearAll={() => { setTradeFilter(''); setCategoryFilter(''); setRegionFilter(''); setCityFilter(''); }}
      />

      <ImageLightbox
        src={lightboxUrl || ''}
        alt="Grooming Photo"
        isOpen={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
      />
    </ReportPageScaffold>
    </CustomReportPanel>
  );
}
